import type { ClientErrorReport } from '@bnb/contracts';
import { BUILD_VERSION } from './build-version.ts';

/**
 * Browser errors → POST /api/client-errors (logged by the API with Powertools; replaces the
 * prototype's Debug log tab). Fire-and-forget, throttled, and it never throws: error
 * reporting must not cause more errors.
 */
export const CLIENT_ERRORS_URL = '/api/client-errors';

// Field limits from clientErrorReportSchema (packages/contracts/src/system.ts).
const LIMITS = { message: 2_000, stack: 10_000, url: 2_000, build: 100 } as const;

function clip(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

/** Shape any thrown value into a schema-valid report. */
export function buildErrorReport(
  error: unknown,
  context: { url: string; build?: string; now?: Date },
): ClientErrorReport {
  let message: string;
  let stack: string | undefined;
  if (error instanceof Error) {
    message = `${error.name}: ${error.message}`;
    stack = error.stack;
  } else if (typeof error === 'string') {
    message = error;
  } else {
    try {
      message = JSON.stringify(error) ?? String(error);
    } catch {
      message = String(error);
    }
  }
  return {
    message: clip(message || 'Unknown error', LIMITS.message),
    ...(stack ? { stack: clip(stack, LIMITS.stack) } : {}),
    // Drop query strings/fragments: they can hold emails or codes.
    url: clip(context.url.split(/[?#]/)[0] ?? '', LIMITS.url),
    build: clip(context.build ?? BUILD_VERSION, LIMITS.build),
    at: (context.now ?? new Date()).toISOString(),
  };
}

/** At most `max` reports per window, and the same message only once per window. */
export function createThrottle(max = 10, windowMs = 60_000, now: () => number = Date.now) {
  let windowStart = now();
  let count = 0;
  const seen = new Set<string>();
  return (key: string): boolean => {
    const t = now();
    if (t - windowStart > windowMs) {
      windowStart = t;
      count = 0;
      seen.clear();
    }
    if (count >= max || seen.has(key)) return false;
    count += 1;
    seen.add(key);
    return true;
  };
}

const allow = createThrottle();

export function reportClientError(error: unknown): void {
  try {
    const report = buildErrorReport(error, { url: window.location.href });
    if (!allow(report.message)) return;
    const body = JSON.stringify(report);
    void fetch(CLIENT_ERRORS_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // never throw from the error reporter
  }
}

let installed = false;

/** Catch uncaught errors and unhandled promise rejections for the whole app. */
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (event) => reportClientError(event.error ?? event.message));
  window.addEventListener('unhandledrejection', (event) => reportClientError(event.reason));
}
